import React, { memo, useCallback } from "react";
import { Box } from "@chakra-ui/react";
import { InView } from "react-intersection-observer";
import { useMutation } from "@apollo/client";
import {
  EditMessageSeenMutation,
  EditMessageSeenMutationVariables,
  MessageThreadFragment,
} from "gql/graphql";
import EDIT_MESSAGE_SEEN_MUTATION from "modules/messaging/_networking/edit-message-seen";
import { usePubSub } from "modules/providers/pub-sub-provider";
import { MESSAGE_LIST_PADDING, MESSAGE_STATE } from "modules/utils/Constants";
import MessageThreadMessage, {
  IMessageFragmentWithIsDraft,
} from "modules/messaging/message-thread/message-thread-message";
import MessageDateSeparator from "modules/messaging/messages/message-date-separator";
import MessageUnreadLine from "modules/messaging/messages/message-unread-line";

interface IProps {
  message: IMessageFragmentWithIsDraft;
  messageThread: MessageThreadFragment;
  dateSeparatorDate?: string;
  isUnseenMessage: boolean;
  showDateSeparator: boolean;
}


const VirtuosoMessage = ({
  message,
  messageThread,
  dateSeparatorDate,
  isUnseenMessage,
  showDateSeparator,
}: IProps) => {
  const { publish } = usePubSub();

  const [editMessageSeenMutationAPI] = useMutation<
    EditMessageSeenMutation,
    EditMessageSeenMutationVariables
  >(EDIT_MESSAGE_SEEN_MUTATION);

  const onInViewChange = useCallback(
    (inView: boolean) => {
      // any visible pixel of the message counts as seen
      if (inView && !message.isSeen) {
        publish("message-seen", message.id);
        editMessageSeenMutationAPI({
          variables: {
            id: message.id,
            isSeen: true,
          },
          optimisticResponse: {
            editMessage: {
              __typename: "Message",
              id: message.id,
              isSeen: true,
            },
          },
        });
      }
    },
    [message.id, message.isSeen, publish, editMessageSeenMutationAPI],
  );

  const messageState: MESSAGE_STATE = message.isDraft ? "not-sent" : "sent";

  return (
    <InView threshold={0} as="div" onChange={onInViewChange}>
      <Box px={MESSAGE_LIST_PADDING}>
        {showDateSeparator && <MessageDateSeparator date={dateSeparatorDate} />}
        {isUnseenMessage && <MessageUnreadLine />}
        <MessageThreadMessage
          messageThread={messageThread}
          message={message}
          messageState={messageState}
        />
      </Box>
    </InView>
  );
};

export default memo(VirtuosoMessage);
